<script setup>
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue';
import { useRoute } from 'vue-router';
import { visibleDocs, chapterForRoute, searchDocs } from '@/helpers/docs';
import { useHelp } from '@/helpers/useHelp';

const route = useRoute();
const help = useHelp();

const query = ref('');
const selectedId = ref(null);
const contentRef = ref(null);

const filteredDocs = computed(() => searchDocs(query.value));

const activeChapter = computed(() => {
  const id = selectedId.value || help.activeChapterId.value;
  return (filteredDocs.value.find((c) => c.id === id))
    || filteredDocs.value[0]
    || visibleDocs()[0];
});

const sectionId = (chapterId, idx) => `help-${chapterId}-${idx}`;

watch(
  () => help.isOpen.value,
  (open) => {
    if (open) {
      const ctxChapter = chapterForRoute(route.path);
      if (!help.activeChapterId.value) help.activeChapterId.value = ctxChapter.id;
      selectedId.value = help.activeChapterId.value;
    }
  }
);

watch(
  () => route.path,
  (newPath) => {
    if (!help.isOpen.value) return;
    const ctxChapter = chapterForRoute(newPath);
    if (ctxChapter) selectedId.value = ctxChapter.id;
  }
);

const selectChapter = (id) => {
  selectedId.value = id;
  help.activeChapterId.value = id;
  nextTick(() => {
    if (contentRef.value) contentRef.value.scrollTop = 0;
  });
};

const scrollToSection = (chapterId, idx) => {
  if (selectedId.value !== chapterId) selectChapter(chapterId);
  nextTick(() => {
    const el = document.getElementById(sectionId(chapterId, idx));
    if (el && contentRef.value) {
      const top = el.offsetTop - 8;
      contentRef.value.scrollTo({ top, behavior: 'smooth' });
    }
  });
};

const onKey = (e) => {
  if (e.key === 'Escape' && help.isOpen.value) {
    help.close();
  } else if (e.key === '?' && !['INPUT', 'TEXTAREA', 'SELECT'].includes(document.activeElement?.tagName)) {
    e.preventDefault();
    help.toggle();
  }
};

onMounted(() => document.addEventListener('keydown', onKey));
onUnmounted(() => document.removeEventListener('keydown', onKey));
</script>

<template>
  <Teleport to="body">
    <transition name="help-fade">
      <div v-if="help.isOpen.value" class="help-backdrop" @click="help.close()"></div>
    </transition>
    <transition name="help-slide">
      <aside v-if="help.isOpen.value" class="help-drawer shadow-lg" role="dialog" aria-label="Hilfe">
        <header class="help-drawer-header d-flex align-items-center gap-2 px-3 py-3 border-bottom">
          <i class="ri-question-line fs-4 text-primary"></i>
          <h5 class="mb-0 fw-semibold flex-grow-1">Hilfe</h5>
          <router-link to="/help" class="btn btn-sm btn-soft-primary" @click="help.close()" title="Vollansicht öffnen">
            <i class="ri-external-link-line"></i>
          </router-link>
          <button type="button" class="btn btn-sm btn-soft-secondary" @click="help.close()" aria-label="Schliessen">
            <i class="ri-close-line"></i>
          </button>
        </header>

        <div class="px-3 py-2 border-bottom">
          <div class="position-relative">
            <i class="ri-search-line position-absolute top-50 translate-middle-y ms-2 text-muted"></i>
            <input v-model="query" type="search" class="form-control ps-4" placeholder="In der Hilfe suchen…" />
          </div>
        </div>

        <div class="help-drawer-body">
          <nav class="help-drawer-nav border-end p-2">
            <template v-for="chapter in filteredDocs" :key="chapter.id">
              <button
                type="button"
                class="btn w-100 text-start rounded-2 px-2 py-2 mb-1 d-flex align-items-center gap-2 chapter-btn"
                :class="activeChapter.id === chapter.id ? 'btn-primary' : 'btn-light text-body'"
                @click="selectChapter(chapter.id)"
              >
                <i :class="chapter.icon" class="fs-5 flex-shrink-0"></i>
                <span class="text-truncate fs-13">{{ chapter.title }}</span>
              </button>
              <ul
                v-if="activeChapter.id === chapter.id && chapter.sections.length > 1"
                class="list-unstyled subnav mb-2 ps-3"
              >
                <li v-for="(sec, i) in chapter.sections" :key="`sn-${chapter.id}-${i}`">
                  <button type="button" class="subnav-link" @click="scrollToSection(chapter.id, i)">
                    {{ sec.heading }}
                  </button>
                </li>
              </ul>
            </template>
            <div v-if="filteredDocs.length === 0" class="text-muted small p-2">Keine Treffer.</div>
          </nav>

          <article ref="contentRef" class="help-drawer-content help-content p-4">
            <header class="help-chapter-header">
              <h3 class="help-chapter-title d-flex align-items-center gap-2">
                <i :class="activeChapter.icon" class="text-primary"></i>
                {{ activeChapter.title }}
              </h3>
              <p v-if="activeChapter.summary" class="help-chapter-summary">{{ activeChapter.summary }}</p>
            </header>
            <section
              v-for="(sec, i) in activeChapter.sections"
              :key="`${activeChapter.id}-${i}`"
              :id="sectionId(activeChapter.id, i)"
              class="help-section"
            >
              <h4 class="help-section-heading">{{ sec.heading }}</h4>
              <div class="help-section-body" v-html="sec.body"></div>
            </section>
          </article>
        </div>
      </aside>
    </transition>
  </Teleport>
</template>

<style scoped>
.help-backdrop {
  position: fixed; inset: 0; background: rgba(0,0,0,0.35); z-index: 1080;
}
.help-drawer {
  position: fixed; top: 0; right: 0; bottom: 0; width: min(880px, 92vw);
  background: var(--vz-body-bg, #fff); z-index: 1081;
  display: flex; flex-direction: column;
}
.help-drawer-body {
  flex: 1 1 auto; min-height: 0;
  display: grid; grid-template-columns: 240px 1fr;
}
.help-drawer-nav { overflow-y: auto; background: var(--vz-light, #f3f3f9); }
.help-drawer-content { overflow-y: auto; scroll-behavior: smooth; }

.subnav { border-left: 2px solid var(--vz-border-color, #e9ebec); margin-left: 14px; padding-left: 10px !important; }
.subnav-link {
  display: block; width: 100%; text-align: left;
  background: transparent; border: 0;
  padding: 3px 6px;
  font-size: 12px; color: var(--vz-secondary, #878a99);
  border-radius: 4px; line-height: 1.35;
}
.subnav-link:hover { color: var(--vz-primary, #405189); background: var(--vz-body-bg, #fff); }

.help-fade-enter-active, .help-fade-leave-active { transition: opacity 0.18s ease; }
.help-fade-enter-from, .help-fade-leave-to { opacity: 0; }
.help-slide-enter-active, .help-slide-leave-active { transition: transform 0.22s ease; }
.help-slide-enter-from, .help-slide-leave-to { transform: translateX(100%); }
@media (max-width: 700px) {
  .help-drawer-body { grid-template-columns: 1fr; }
  .help-drawer-nav { display: block; max-height: 35vh; }
}
</style>

<!-- Global styles shared by the drawer and the /help page -->
<style>
.help-content {
  color: var(--vz-body-color);
  line-height: 1.6;
  font-size: 14px;
}
.help-chapter-header {
  padding-bottom: 1rem;
  margin-bottom: 1.5rem;
  border-bottom: 1px solid var(--vz-border-color, #e9ebec);
}
.help-chapter-title {
  font-weight: 700;
  letter-spacing: -0.01em;
  margin-bottom: 0.35rem;
}
.help-chapter-summary {
  color: var(--vz-secondary-color, #878a99);
  font-size: 0.95em;
  margin-bottom: 0;
}
.help-section {
  padding-top: 1.25rem;
  margin-top: 1.25rem;
  border-top: 1px dashed var(--vz-border-color, #e9ebec);
  scroll-margin-top: 12px;
}
.help-section:first-of-type {
  border-top: 0;
  margin-top: 0;
  padding-top: 0;
}
.help-section-heading {
  font-size: 1.05rem;
  font-weight: 700;
  color: var(--vz-heading-color, var(--vz-body-color));
  margin: 0 0 0.6rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
.help-section-heading::before {
  content: "";
  display: inline-block;
  width: 4px;
  height: 1.05em;
  background: var(--vz-primary, #405189);
  border-radius: 2px;
  flex-shrink: 0;
}
.help-section-body { font-size: 13px; color: var(--vz-body-color); }
.help-section-body p { margin: 0 0 0.6rem 0; }
.help-section-body p:last-child { margin-bottom: 0; }
.help-section-body ul, .help-section-body ol { margin: 0 0 0.6rem 1.1rem; padding: 0; }
.help-section-body li { margin-bottom: 0.15rem; }

.help-section-body dl {
  display: grid;
  grid-template-columns: minmax(140px, 28%) 1fr;
  column-gap: 1rem;
  row-gap: 0.55rem;
  margin: 0;
}
.help-section-body dt {
  font-weight: 600;
  color: var(--vz-heading-color, var(--vz-body-color));
  background: var(--vz-light, #f3f3f9);
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  align-self: start;
  font-size: 12.5px;
}
.help-section-body dd {
  margin: 0;
  color: var(--vz-body-color);
  align-self: center;
}
.help-section-body code {
  background: var(--vz-light, #f3f3f9);
  padding: 0.05rem 0.35rem;
  border-radius: 3px;
  font-size: 0.88em;
  color: var(--vz-primary, #405189);
}
.help-section-body b, .help-section-body strong {
  color: var(--vz-heading-color, var(--vz-body-color));
  font-weight: 600;
}

@media (max-width: 600px) {
  .help-section-body dl { grid-template-columns: 1fr; row-gap: 0.2rem; }
  .help-section-body dd { padding-left: 0.25rem; padding-bottom: 0.4rem; border-bottom: 1px dotted var(--vz-border-color, #e9ebec); }
}
</style>

